import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

type Props = {
  title: string;
  /** Optional node rendered next to the title — used for a status badge etc. */
  titleSuffix?: ReactNode;
  /** Optional action(s) rendered to the right of the title, before the Back button.
   *  Use for page-scoped actions like "Preview" or "Export". */
  actions?: ReactNode;
  /** Called when the user clicks the Back button. */
  onBack: () => void;
};

/**
 * Title-on-the-left, text-button-Back-on-the-right page header. Used at the top of
 * the Workflow Details, Approver Review, and Version History pages. Keeps the
 * spacing/layout identical between them.
 */
export function PageHeader({ title, titleSuffix, actions, onBack }: Props) {
  const { t } = useTranslation();
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {/* Semantically the page's <h1> (WCAG 2.1 — 1.3.1, 2.4.6). Pages used to
            open at h3 with no h1 anywhere, which breaks navigate-by-heading —
            a primary screen-reader technique — and reads as a document whose
            top two levels are missing. `level={1}` with the level-3 type scale
            pinned back on, so the semantics change and the design does not. */}
        <Title level={1} style={{ margin: 0, fontSize: 24, lineHeight: 1.35, fontWeight: 600 }}>
          {title}
        </Title>
        {titleSuffix && (
          <>
            {/* Subtle en-dash separator so the suffix (status badge) reads like part of
                the title, not an afterthought floating next to it. */}
            {/* Decorative punctuation — hidden from assistive tech (it would be
                read as "en dash" between the title and its status) and darkened
                to clear 4.5:1 for the sighted reader (1.4.3). */}
            <span
              aria-hidden
              style={{ color: '#6b7280', fontSize: 22, fontWeight: 400, lineHeight: 1 }}
            >
              –
            </span>
            {titleSuffix}
          </>
        )}
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {actions}
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
          {t('common.back')}
        </Button>
      </div>
    </div>
  );
}
